import React, { useState, useEffect } from 'react';
import { Search, BookOpen, Eye, Heart, Clock, User, Trash2, Image, Video, Type, MapPin, X, ToggleLeft, ToggleRight, Users2, RefreshCw, Archive } from 'lucide-react';
import axios from 'axios';

interface Story {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    family_id: number;
    family_name: string;
    media_type: string;
    media_url?: string;
    text_content?: string;
    gradient_type?: string;
    latitude?: number;
    longitude?: number;
    location_name?: string;
    is_active: boolean;
    view_count: number;
    like_count: number;
    created_at: string;
    expires_at: string;
    user_language?: string;
}

interface StoryStats {
    total_stories: number;
    active_stories: number;
    period_stories: number;
    total_views: number;
    total_likes: number;
    by_media_type: { type: string; count: number }[];
    top_creators: { user_id: number; username: string; full_name?: string; count: number }[];
}

// Stat Card
const StatCard: React.FC<{ icon: React.ReactNode; value: number; label: string; color: string }> = ({ icon, value, label, color }) => {
    const colors: Record<string, string> = {
        pink: 'from-pink-50 to-pink-100 border-pink-200 text-pink-700',
        green: 'from-green-50 to-green-100 border-green-200 text-green-700',
        blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
        red: 'from-red-50 to-red-100 border-red-200 text-red-600',
        purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    };

    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
            <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
            <p className="text-xl font-semibold">{value}</p>
            <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
        </div>
    );
};

const getFlag = (lang?: string) => {
    const f: Record<string, string> = { 'tr': '🇹🇷', 'en': '🇬🇧', 'de': '🇩🇪', 'es': '🇪🇸', 'fr': '🇫🇷', 'it': '🇮🇹', 'pt': '🇵🇹', 'nl': '🇳🇱', 'ru': '🇷🇺', 'ar': '🇸🇦', 'zh': '🇨🇳', 'ja': '🇯🇵', 'ko': '🇰🇷' };
    return lang ? f[lang] || '🌐' : null;
};

const StoryManagement: React.FC = () => {
    const [stories, setStories] = useState<Story[]>([]);
    const [stats, setStats] = useState<StoryStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Story | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [tab, setTab] = useState<'all' | 'active' | 'archive'>('all');
    const [limit, setLimit] = useState(50);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchStories();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStories = async () => {
        setLoading(true);
        setRefreshing(true);
        try {
            const r = await axios.get('https://api.lifeshare.app/api/v1/admin/stories?active_only=false');
            setStories(r.data.stories || []);
        } catch (e) { console.error(e); }
        setLoading(false);
        setRefreshing(false);
    };

    const fetchStats = async () => {
        try {
            const r = await axios.get('https://api.lifeshare.app/api/v1/admin/stories/stats?days=30');
            setStats(r.data);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu hikayeyi kalıcı olarak silmek istiyor musunuz? Bu işlem geri alınamaz!')) return;
        setActionLoading(id);
        try {
            await axios.delete(`https://api.lifeshare.app/api/v1/admin/stories/${id}`);
            setStories(stories.filter(s => s.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch (e: any) { alert('Silme başarısız: ' + (e.response?.data?.detail || 'Hata')); }
        setActionLoading(null);
    };

    const handleToggle = async (id: number) => {
        setActionLoading(id);
        try {
            const r = await axios.put(`https://api.lifeshare.app/api/v1/admin/stories/${id}/toggle`, {});
            setStories(stories.map(s => s.id === id ? { ...s, is_active: r.data.is_active } : s));
            if (selected?.id === id) setSelected({ ...selected, is_active: r.data.is_active });
        } catch (e) { alert('İşlem başarısız'); }
        setActionLoading(null);
    };

    const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

    const tabFiltered = tab === 'all' ? stories : tab === 'active' ? stories.filter(s => s.is_active && !isExpired(s.expires_at)) : stories.filter(s => !s.is_active || isExpired(s.expires_at));

    const filtered = tabFiltered.filter(s =>
        s.user_username.toLowerCase().includes(search.toLowerCase()) ||
        s.family_name.toLowerCase().includes(search.toLowerCase()) ||
        s.text_content?.toLowerCase().includes(search.toLowerCase()) ||
        s.location_name?.toLowerCase().includes(search.toLowerCase())
    );

    const getGradient = (type?: string) => {
        switch (type) {
            case 'purple': return 'from-purple-500 to-pink-500';
            case 'blue': return 'from-blue-500 to-cyan-500';
            case 'green': return 'from-green-500 to-emerald-500';
            default: return 'from-orange-500 to-red-500';
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Hikayeler</h1>
                        <p className="text-sm text-gray-500 mt-1">{stories.length} hikaye</p>
                    </div>
                    <button onClick={fetchStories} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
                        <StatCard icon={<BookOpen />} value={stats.total_stories} label="Toplam" color="pink" />
                        <StatCard icon={<ToggleRight />} value={stats.active_stories} label="Aktif" color="green" />
                        <StatCard icon={<Eye />} value={stats.total_views} label="Görüntülenme" color="blue" />
                        <StatCard icon={<Heart />} value={stats.total_likes} label="Beğeni" color="red" />
                        <StatCard icon={<Clock />} value={stats.period_stories} label="30 Gün" color="purple" />
                    </div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl p-4 mb-6">
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Kullanıcı, çevre veya konum ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        {(['all', 'active', 'archive'] as const).map(t => (
                            <button key={t} onClick={() => { setTab(t); setLimit(50); }}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === t ? (t === 'archive' ? 'bg-orange-500 text-white' : t === 'active' ? 'bg-green-500 text-white' : 'bg-purple-600 text-white') : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {t === 'all' && <BookOpen className="w-3.5 h-3.5" />}
                                {t === 'active' && <ToggleRight className="w-3.5 h-3.5" />}
                                {t === 'archive' && <Archive className="w-3.5 h-3.5" />}
                                {t === 'all' ? 'Tümü' : t === 'active' ? 'Aktif' : 'Arşiv'}
                                <span className="text-xs opacity-70">({(t === 'all' ? stories : t === 'active' ? stories.filter(s => s.is_active && !isExpired(s.expires_at)) : stories.filter(s => !s.is_active || isExpired(s.expires_at))).length})</span>
                            </button>
                        ))}
                        <span className="ml-auto text-xs text-gray-500 self-center">{filtered.length} sonuç</span>
                    </div>
                </div>

                {/* Stories Grid - Instagram Story Style */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {filtered.slice(0, limit).map(s => (
                        <div key={s.id} className="cursor-pointer group" onClick={() => setSelected(s)}>
                            {/* Story Ring */}
                            <div className={`p-0.5 rounded-2xl ${s.is_active && !isExpired(s.expires_at) ? 'bg-gradient-to-tr from-pink-500 via-purple-500 to-blue-500' : 'bg-gray-300'}`}>
                                <div className="aspect-[9/16] rounded-[14px] overflow-hidden bg-gray-100 relative">
                                    {/* Media */}
                                    {s.media_type === 'photo' && s.media_url ? (
                                        <img src={`https://api.lifeshare.app${s.media_url}`} alt="" className="w-full h-full object-cover" />
                                    ) : s.media_type === 'text' ? (
                                        <div className={`w-full h-full flex items-center justify-center p-2 bg-gradient-to-br ${getGradient(s.gradient_type)}`}>
                                            <p className="text-white text-center text-[10px] font-medium line-clamp-4">{s.text_content}</p>
                                        </div>
                                    ) : s.media_type === 'video' ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                            <Video className="w-6 h-6 text-white" />
                                        </div>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                            <BookOpen className="w-6 h-6 text-gray-400" />
                                        </div>
                                    )}

                                    {/* Overlay on hover */}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <div className="flex items-center gap-3 text-white text-xs">
                                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{s.view_count}</span>
                                            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{s.like_count}</span>
                                        </div>
                                    </div>

                                    {/* Status Badge */}
                                    {(!s.is_active || isExpired(s.expires_at)) && (
                                        <div className="absolute top-1 right-1">
                                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium ${!s.is_active ? 'bg-red-500 text-white' : 'bg-gray-500 text-white'}`}>
                                                {!s.is_active ? 'Pasif' : 'Süresi Doldu'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Media Type Icon */}
                                    <div className="absolute bottom-1 left-1">
                                        <span className="w-5 h-5 bg-black/50 rounded flex items-center justify-center">
                                            {s.media_type === 'photo' ? <Image className="w-3 h-3 text-white" /> :
                                                s.media_type === 'video' ? <Video className="w-3 h-3 text-white" /> :
                                                    <Type className="w-3 h-3 text-white" />}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Username + Details */}
                            <div className="mt-2">
                                <p className="text-xs font-medium text-gray-900 truncate">{s.user_name || s.user_username}</p>
                                <p className="text-[10px] text-gray-500 truncate">{s.family_name} {getFlag(s.user_language)}</p>
                                <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
                                    <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{s.view_count}</span>
                                    <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{s.like_count}</span>
                                    <span className="ml-auto">{new Date(s.created_at).toLocaleDateString('tr-TR')}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Load More */}
                {filtered.length > limit && (
                    <div className="text-center mt-6">
                        <button onClick={() => setLimit(l => l + 50)} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
                            Daha fazla yükle ({limit} / {filtered.length})
                        </button>
                    </div>
                )}

                {/* Empty */}
                {filtered.length === 0 && (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Hikaye bulunamadı</p>
                    </div>
                )}
            </div>

            {/* Modal - Full Story View */}
            {selected && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
                    <div className="relative w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>

                        {/* Story Container */}
                        <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-gray-900 relative">
                            {/* Media */}
                            {selected.media_type === 'photo' && selected.media_url ? (
                                <img src={`https://api.lifeshare.app${selected.media_url}`} alt="" className="w-full h-full object-cover" />
                            ) : selected.media_type === 'text' ? (
                                <div className={`w-full h-full flex items-center justify-center p-6 bg-gradient-to-br ${getGradient(selected.gradient_type)}`}>
                                    <p className="text-white text-center text-xl font-medium">{selected.text_content}</p>
                                </div>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Video className="w-16 h-16 text-white/50" />
                                </div>
                            )}

                            {/* Top Overlay */}
                            <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent">
                                {/* User */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <span className="text-white font-semibold">{(selected.user_name || selected.user_username).charAt(0).toUpperCase()}</span>
                                    </div>
                                    <div>
                                        <p className="text-white font-medium text-sm">{selected.user_name || selected.user_username}</p>
                                        <p className="text-white/60 text-xs">{new Date(selected.created_at).toLocaleDateString('tr-TR')} • {selected.family_name}</p>
                                    </div>
                                    {getFlag(selected.user_language) && <span className="ml-auto">{getFlag(selected.user_language)}</span>}
                                </div>
                            </div>

                            {/* Bottom Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                                {/* Stats */}
                                <div className="flex items-center gap-4 mb-3">
                                    <span className="flex items-center gap-1.5 text-white text-sm"><Eye className="w-4 h-4" />{selected.view_count}</span>
                                    <span className="flex items-center gap-1.5 text-white text-sm"><Heart className="w-4 h-4" />{selected.like_count}</span>
                                    {selected.location_name && (
                                        <span className="flex items-center gap-1.5 text-white/60 text-xs ml-auto"><MapPin className="w-3 h-3" />{selected.location_name}</span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="flex gap-2">
                                    <span className={`px-2.5 py-1 rounded-xl text-xs font-medium ${selected.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                                        {selected.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                    {isExpired(selected.expires_at) && <span className="px-2.5 py-1 rounded-xl text-xs font-medium bg-gray-500 text-white">Süresi Doldu</span>}
                                </div>
                            </div>

                            {/* Close */}
                            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center">
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => handleToggle(selected.id)}
                                disabled={actionLoading === selected.id}
                                className={`flex-1 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 ${selected.is_active ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}
                            >
                                {selected.is_active ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                                {selected.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                            </button>
                            <button
                                onClick={() => handleDelete(selected.id)}
                                disabled={actionLoading === selected.id}
                                className="py-2.5 px-4 bg-red-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1.5"
                            >
                                {actionLoading === selected.id ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoryManagement;
